/**
 * Client Service
 * Handles client organization management API calls (for Client Admins)
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface Client {
  id: string;
  name: string;
  description: string;
  passwordExpirationDays?: number;
  enableApiKeyAccess: boolean;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface ClientUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  lastLoginAt?: string;
  passwordExpiresAt?: string;
  passwordExpired: boolean;
  createdAt: string;
  markedForDeletionAt?: string;
  canAccessNmvtis?: boolean;
  canAccessTheftLien?: boolean;
  canAccessMotorVehicleRecord?: boolean;
  canAccessVinDecoder?: boolean;
  canAccessAstsPublic?: boolean;
  canAccessInvoicing?: boolean;
  canManageClients?: boolean;
  mfaEnabled?: boolean;
  isSystemAdmin?: boolean;
  receivePortalAlerts?: boolean;
}

export interface UpdatePoliciesRequest {
  passwordExpirationDays?: number;
  enableApiKeys: boolean;
}

export interface InviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Get client information
 */
export const getClient = async (clientId: string): Promise<Client> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.GET(clientId));

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Get the current user's client DPPA permissible use code
 */
export const getMyDppa = async (): Promise<string | null> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.MY_DPPA);
  if (!response.ok) return null;
  const data = await response.json();
  return data.mvrDppaPermissibleUse ?? null;
};

/**
 * Update client security policies
 */
export const updateClientPolicies = async (
  clientId: string,
  policies: UpdatePoliciesRequest
): Promise<Client> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.UPDATE_POLICIES(clientId), {
    method: 'PATCH',
    body: JSON.stringify(policies),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * List client users
 */
export const listClientUsers = async (clientId: string): Promise<ClientUser[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.LIST_USERS(clientId));

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

export interface MyPricingService {
  serviceType: string;
  effectivePrice: number;
}

export interface MyPricingMvrState {
  stateCode: string;
  stateName: string;
  effectivePrice: number;
}

export interface MyPricing {
  services: MyPricingService[];
  mvrByState: MyPricingMvrState[];
}

/**
 * Get pricing for the current user's organization (client admin)
 */
export const getMyPricing = async (): Promise<MyPricing> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.MY_PRICING);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Invite new user to client organization
 */
export const inviteUser = async (clientId: string, userData: InviteUserRequest): Promise<ClientUser> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS.INVITE_USER(clientId), {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
